import React from 'react';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import TableComponent from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Grid from '@mui/material/Unstable_Grid2';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';

import gpConsultationsPricing from './gp_consultations_pricing.json';
import nurseConsultationsPricing from './nurse_consultations_pricing.json';
import prescriptionsPricing from './prescriptions_pricing.json';
import servicesFeesPricing from './services_fees_pricing.json';
import restHomePricing from './rest_home_pricing.json';
import additionalFeesPricing from './additional_fees_pricing.json';

import './style.scss';

function Services() {
  const renderLabel = (item) => {
    if (!item) return '';
    if (typeof item === 'object') {
      const { label, popup } = item;
      if (popup) {
        return (
          <Tooltip title={popup} arrow>
            <span className="tooltip-label">
              {label}
              <InfoIcon sx={{ color: 'inherit', fontSize: 14 }} />
            </span>
          </Tooltip>
        );
      }
      return label;
    }
    return item;
  };

  const renderConsultationTable = (tableData) => {
    return (
      <div key={tableData.title} className="consultation-table-section">
        <div className="table-section-header">
          <h3>
            {tableData.title}
            {tableData.subtitle && <span className="subtitle"> ({tableData.subtitle})</span>}
            {tableData.duration && <span className="duration"> [{tableData.duration}]</span>}
          </h3>
        </div>

        {tableData.note && (
          <p className="table-note">{renderLabel(tableData.note)}</p>
        )}

        <TableContainer component={Paper} className="table-container">
          <TableComponent size="small" aria-label={tableData.title}>
            <TableHead>
              <TableRow>
                <TableCell className="age-group-header-placeholder" />
                {tableData.headers.map((h, index) => (
                  <TableCell 
                    key={index} 
                    colSpan={h.subheaders.length} 
                    align="center" 
                    className="main-header-cell"
                  >
                    {renderLabel(h.label)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="sub-header-cell">Age Group</TableCell>
                {tableData.headers.map((h) => 
                  h.subheaders.map((sub, subIndex) => (
                    <TableCell key={subIndex} align="center" className="sub-header-cell">
                      {renderLabel(sub)}
                    </TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell className="age-cell">{row.age}</TableCell>
                  {row.prices.map((price, priceIndex) => (
                    <TableCell key={priceIndex} align="center" className="price-cell">
                      {price}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </TableComponent>
        </TableContainer>
        {tableData.subNote && <p className="table-subnote">{renderLabel(tableData.subNote)}</p>}
      </div>
    );
  };

  const halfIndex = Math.ceil(servicesFeesPricing.length / 2);
  const leftColumnFees = servicesFeesPricing.slice(0, halfIndex);
  const rightColumnFees = servicesFeesPricing.slice(halfIndex);

  return (
    <div className="component-services">
      <div className="content">
        <div className="table-header">
          <h2>Leamington Medical Centre Consultation & Service Fees</h2>
        </div>

        {/* GP Consultations */}
        {gpConsultationsPricing.map(renderConsultationTable)}

        {/* Nurse Consultations */}
        {nurseConsultationsPricing.map(renderConsultationTable)}

        {/* Services Fees Section */}
        <div className="services-fees-section">
          <div className="table-section-header">
            <h3>Services Fees <span className="subtitle">[additional charges may apply]</span></h3>
          </div>
          <Grid container spacing={2} className="services-fees-grid">
            <Grid xs={12} md={6}>
              <TableContainer component={Paper} className="table-container">
                <TableComponent size="small">
                  <TableBody>
                    {leftColumnFees.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="desc-cell">{renderLabel(item)}</TableCell>
                        <TableCell align="right" className="price-value">
                          {item.price}
                          {item.note && <span className="item-note"> ({item.note})</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableComponent>
              </TableContainer>
            </Grid>
            <Grid xs={12} md={6}>
              <TableContainer component={Paper} className="table-container">
                <TableComponent size="small">
                  <TableBody>
                    {rightColumnFees.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="desc-cell">{renderLabel(item)}</TableCell>
                        <TableCell align="right" className="price-value">
                          {item.price}
                          {item.note && <span className="item-note"> ({item.note})</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableComponent>
              </TableContainer>
            </Grid>
          </Grid>
        </div>

        {/* Bottom Sections Grid */}
        <Grid container spacing={2} className="bottom-sections-grid">
          {/* Left Side: Prescriptions & DNA */}
          <Grid xs={12} md={6}>
            <div className="bottom-section-block">
              <div className="table-section-header">
                <h3>Repeat Prescriptions</h3>
              </div>
              <TableContainer component={Paper} className="table-container">
                <TableComponent size="small">
                  <TableBody>
                    {prescriptionsPricing.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="desc-cell">{renderLabel(item)}</TableCell>
                        <TableCell align="right" className="price-value">
                          {item.price}
                          {item.suffix && <span className="suffix"> {item.suffix}</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableComponent>
              </TableContainer>
              <p className="note-text">Note, repeat scripts aim to be ready within 48 hours</p>
            </div>

            <div className="bottom-section-block dna-block">
              <div className="table-section-header danger-header">
                <h3>{additionalFeesPricing.dna.title}</h3>
              </div>
              <TableContainer component={Paper} className="table-container">
                <TableComponent size="small">
                  <TableBody>
                    {additionalFeesPricing.dna.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="desc-cell dna-item">{renderLabel(item)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableComponent>
              </TableContainer>
            </div>
          </Grid>

          {/* Right Side: Rest Home Related Fees & General Notes */}
          <Grid xs={12} md={6}>
            <div className="bottom-section-block">
              <div className="table-section-header">
                <h3>Rest Home Related Fees</h3>
              </div>
              <TableContainer component={Paper} className="table-container">
                <TableComponent size="small">
                  <TableBody>
                    {restHomePricing.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="desc-cell">{renderLabel(item)}</TableCell>
                        <TableCell align="right" className="price-value">{item.price}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableComponent>
              </TableContainer>
            </div>

            <div className="bottom-section-block notes-block">
              <div className="table-section-header info-header">
                <h3>Important Notice & Information</h3>
              </div>
              <div className="general-notes-content">
                {additionalFeesPricing.generalNotes.map((note, index) => (
                  <p key={index} className="note-paragraph">{renderLabel(note)}</p>
                ))}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default Services;
